/**
 * usePushSubscription · TODO-MDC-20
 *
 * Hook que orquesta el ciclo de Web Push notifications:
 *   1. Verifica soporte del browser (Notification + ServiceWorker + PushManager).
 *   2. Lee permiso actual ("default" | "granted" | "denied").
 *   3. Lee suscripción actual del SW (si la hay).
 *   4. Expone `enable()` que pide permiso, registra el SW si no está,
 *      crea la suscripción contra el push manager con la VAPID public
 *      key, y persiste en BD via RPC `upsert_push_subscription`.
 *   5. Expone `disable()` que cancela la suscripción local + RPC delete.
 *
 * Diseño:
 *   - El SW vive en /sw.js y se registra al primer enable. Lo registramos
 *     lazy (no en el bootstrap de la app) para no agregar latencia a
 *     usuarios que nunca van a usar push.
 *   - VAPID public key viene de import.meta.env.VITE_VAPID_PUBLIC_KEY.
 *     Si no está definida, el hook reporta `unsupported = true` con
 *     reason = 'no_vapid_key' (estado configuracional, no error de UX).
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type PushPermission = 'default' | 'granted' | 'denied';

export interface PushState {
  supported: boolean;
  reason?:
    | 'no_vapid_key'
    | 'no_notification_api'
    | 'no_service_worker'
    | 'no_push_manager';
  permission: PushPermission;
  /** Hay suscripción activa en este dispositivo */
  subscribed: boolean;
  /** En medio de un enable() o disable() */
  busy: boolean;
  /** Último error legible para mostrar al usuario */
  error: string | null;
}

const initialState: PushState = {
  supported: false,
  permission: 'default',
  subscribed: false,
  busy: false,
  error: null,
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i);
  return arr;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function usePushSubscription(empresaId?: string | null) {
  const [state, setState] = useState<PushState>(initialState);

  const detectSupport = useCallback(async () => {
    if (!VAPID_PUBLIC_KEY) {
      setState((s) => ({ ...s, supported: false, reason: 'no_vapid_key' }));
      return false;
    }
    if (typeof Notification === 'undefined') {
      setState((s) => ({ ...s, supported: false, reason: 'no_notification_api' }));
      return false;
    }
    if (!('serviceWorker' in navigator)) {
      setState((s) => ({ ...s, supported: false, reason: 'no_service_worker' }));
      return false;
    }
    if (!('PushManager' in window)) {
      setState((s) => ({ ...s, supported: false, reason: 'no_push_manager' }));
      return false;
    }
    setState((s) => ({ ...s, supported: true, reason: undefined }));
    return true;
  }, []);

  const refreshSubscriptionStatus = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) {
        setState((s) => ({
          ...s,
          subscribed: false,
          permission: Notification.permission as PushPermission,
        }));
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      setState((s) => ({
        ...s,
        subscribed: sub !== null,
        permission: Notification.permission as PushPermission,
      }));
    } catch {
      setState((s) => ({
        ...s,
        subscribed: false,
        permission: 'default' as PushPermission,
      }));
    }
  }, []);

  // Detección inicial.
  useEffect(() => {
    void (async () => {
      const ok = await detectSupport();
      if (ok) await refreshSubscriptionStatus();
    })();
  }, [detectSupport, refreshSubscriptionStatus]);

  // Escuchar pushsubscriptionchange del SW.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data as { kind?: string } | null;
      if (data?.kind === 'pushsubscriptionchange') {
        void enable(); // re-suscribir silenciosamente
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!state.supported) return false;
    if (!VAPID_PUBLIC_KEY) {
      setState((s) => ({ ...s, error: 'VAPID public key no configurada' }));
      return false;
    }
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      // 1. Pedir permiso (browser muestra prompt).
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setState((s) => ({
          ...s,
          busy: false,
          permission: perm as PushPermission,
          subscribed: false,
          error: perm === 'denied' ? 'Permiso denegado por el navegador' : null,
        }));
        return false;
      }

      // 2. Registrar SW (idempotente — si ya está, devuelve la registration).
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;

      // 3. Crear (o re-usar) la suscripción.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          // Cast: TS 5.7 endurece BufferSource pero PushManager acepta Uint8Array.
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
        });
      }

      // 4. Persistir en BD.
      const p256dh = sub.getKey('p256dh');
      const auth = sub.getKey('auth');
      if (!p256dh || !auth) throw new Error('Suscripción sin keys p256dh/auth');

      const { error } = await supabase.rpc('upsert_push_subscription', {
        p_endpoint: sub.endpoint,
        p_p256dh: arrayBufferToBase64(p256dh),
        p_auth: arrayBufferToBase64(auth),
        p_empresa_id: empresaId ?? undefined,
        p_user_agent: navigator.userAgent.slice(0, 500),
      });
      if (error) throw new Error(error.message);

      setState((s) => ({
        ...s,
        busy: false,
        subscribed: true,
        permission: 'granted',
        error: null,
      }));
      return true;
    } catch (e) {
      setState((s) => ({
        ...s,
        busy: false,
        error: (e as Error).message ?? 'Error al activar notificaciones',
      }));
      return false;
    }
  }, [empresaId, state.supported]);

  const disable = useCallback(async (): Promise<boolean> => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          // Borrar de BD primero (mientras tenemos endpoint).
          await supabase.rpc('delete_push_subscription', { p_endpoint: sub.endpoint });
          await sub.unsubscribe();
        }
      }
      setState((s) => ({ ...s, busy: false, subscribed: false, error: null }));
      return true;
    } catch (e) {
      setState((s) => ({
        ...s,
        busy: false,
        error: (e as Error).message ?? 'Error al desactivar',
      }));
      return false;
    }
  }, []);

  return { state, enable, disable, refresh: refreshSubscriptionStatus };
}
